import {
  HttpContextToken,
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandler,
  type HttpInterceptor,
  type HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, switchMap, throwError, type Observable } from 'rxjs';
import { isAuthEligibleRequest } from '../helpers/is-auth-eligible-request/is-auth-eligible-request.helper';
import { AuthSessionService } from '../services/auth-session.service';
import { AuthService } from '../services/auth.service';
import * as AuthActions from '../store/actions/auth.actions';

const hasAuthRefreshRetry = new HttpContextToken<boolean>(() => false);
const UNAUTHORIZED_HTTP_STATUS_CODE = 401;
const BEARER_AUTHORIZATION_PREFIX = 'Bearer ';

const getAccessTokenFromRequest = (
  request: HttpRequest<unknown>,
): string | undefined => {
  const authorizationHeader = request.headers.get('Authorization');

  if (authorizationHeader?.startsWith(BEARER_AUTHORIZATION_PREFIX) !== true) {
    return undefined;
  }

  return authorizationHeader.slice(BEARER_AUTHORIZATION_PREFIX.length);
};

const isUnauthorizedResponse = (error: unknown): error is HttpErrorResponse => {
  return (
    error instanceof HttpErrorResponse &&
    error.status === UNAUTHORIZED_HTTP_STATUS_CODE
  );
};

/** @internal */
export const getAuthorizationHeaderValue = (accessToken: string): string => {
  return `${BEARER_AUTHORIZATION_PREFIX}${accessToken}`;
};

const hasNonEmptyToken = (token: string | undefined): token is string => {
  return token !== undefined && token.length > 0;
};

@Injectable()
export class AuthTokenRefreshInterceptor implements HttpInterceptor {
  readonly #authService = inject(AuthService);
  readonly #authSessionService = inject(AuthSessionService);
  readonly #store = inject(Store);

  intercept(
    request: HttpRequest<unknown>,
    handler: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return handler.handle(request).pipe(
      catchError((error: unknown) => {
        if (!isUnauthorizedResponse(error)) {
          return throwError(() => error);
        }

        if (!isAuthEligibleRequest(request.url)) {
          return throwError(() => error);
        }

        if (request.context.get(hasAuthRefreshRetry)) {
          this.#store.dispatch(AuthActions.unauthorizedRequest());

          return throwError(() => error);
        }

        const requestAccessToken = getAccessTokenFromRequest(request);
        const accessToken = this.#authService.getAccessToken();
        const rotatedAccessToken = this.#getRotatedAccessToken(
          requestAccessToken,
          accessToken,
        );

        if (rotatedAccessToken !== undefined) {
          return this.#handleRetriedRequest(
            request.clone({
              context: request.context.set(hasAuthRefreshRetry, true),
              setHeaders: {
                Authorization: getAuthorizationHeaderValue(rotatedAccessToken),
              },
            }),
            handler,
          );
        }

        return this.#authSessionService.refreshAccessToken().pipe(
          switchMap((refreshedAccessToken) => {
            const retriedRequest = request.clone({
              context: request.context.set(hasAuthRefreshRetry, true),
              setHeaders: {
                Authorization:
                  getAuthorizationHeaderValue(refreshedAccessToken),
              },
            });

            return this.#handleRetriedRequest(retriedRequest, handler);
          }),
          catchError(() => throwError(() => error)),
        );
      }),
    );
  }

  #getRotatedAccessToken(
    requestAccessToken: string | undefined,
    accessToken: string | undefined,
  ): string | undefined {
    if (
      hasNonEmptyToken(requestAccessToken) &&
      hasNonEmptyToken(accessToken) &&
      requestAccessToken !== accessToken
    ) {
      return accessToken;
    }

    return undefined;
  }

  #handleRetriedRequest(
    request: HttpRequest<unknown>,
    handler: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return handler.handle(request).pipe(
      catchError((error: unknown) => {
        if (isUnauthorizedResponse(error)) {
          this.#store.dispatch(AuthActions.unauthorizedRequest());
        }

        return throwError(() => error);
      }),
    );
  }
}
